import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	findBranchKeysContaining,
	navbarBranchQueryKeys,
	navbarTreeQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { trashQueryKeys } from "@src/components/board/queries/query-keys";
import { graphql } from "@src/gql/gql";
import type { NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const deleteFolderMutation = graphql(/* GraphQL */ `
	mutation deleteFolder($section: NavbarSection!, $key: String!) {
		navbar {
			deleteFolder(section: $section, folderKey: $key)
		}
	}
`);

type DeleteFolderInput = {
	section: NavbarSection;
	key: string;
};

type SnapshotEntry = {
	queryKey: readonly unknown[];
	items: NavbarBranchItem[] | undefined;
};

export function useDeleteFolder() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ section, key }: DeleteFolderInput) => {
			const response = await client.mutate({
				mutation: deleteFolderMutation,
				variables: { section, key },
			});
			return response.data?.navbar?.deleteFolder ?? false;
		},
		onMutate: async ({ section, key }) => {
			// Find every branch cache that currently lists this folder.
			// In practice there should only be one, but cope with shared sections.
			const containingKeys = findBranchKeysContaining(queryClient, key);
			const snapshots: SnapshotEntry[] = [];

			for (const queryKey of containingKeys) {
				await queryClient.cancelQueries({ queryKey });
				const items = queryClient.getQueryData<NavbarBranchItem[]>(queryKey);
				snapshots.push({ queryKey, items });
				queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) =>
					old ? old.filter((item) => item.key !== key) : old,
				);
			}

			// Also drop any cached children list for the folder itself — it's
			// being deleted, no point keeping an orphan branch cache around.
			const childrenQueryKey = navbarBranchQueryKeys.branch(section, key);
			const childrenSnapshot =
				queryClient.getQueryData<NavbarBranchItem[]>(childrenQueryKey);
			if (childrenSnapshot !== undefined) {
				snapshots.push({
					queryKey: childrenQueryKey,
					items: childrenSnapshot,
				});
				queryClient.removeQueries({ queryKey: childrenQueryKey });
			}

			return { snapshots };
		},
		onError: (error, _variables, context) => {
			for (const snap of context?.snapshots ?? []) {
				queryClient.setQueryData(snap.queryKey, snap.items);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, { section }, context) => {
			for (const snap of context?.snapshots ?? []) {
				queryClient.invalidateQueries({ queryKey: snap.queryKey });
			}
			// Refresh the whole-tree cache so any consumer of it also converges.
			queryClient.invalidateQueries({
				queryKey: navbarTreeQueryKeys.tree(section),
			});
			void queryClient.invalidateQueries({
				queryKey: trashQueryKeys.all(),
			});
		},
	});
}
